import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import { toast } from "sonner";
import { CustomerForm } from "./CustomerForm";
import type { CustomerFormData } from "../../../shared/schemas/customer.schema";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Building2 } from "lucide-react";
import { ModalBreadcrumbHeader } from "@/components/shared/ModalBreadcrumbHeader";

export interface CustomerFormModalProps {
  open?: boolean;
  onClose?: () => void;
  onSuccess?: (newItem: { id: string; label: string; original?: any }) => void;
  onCancel?: () => void;
  customerId?: string;
  mode?: "create" | "edit";
  initialData?: Partial<CustomerFormData> & { name?: string; code?: string; [key: string]: any };
  lockedFields?: string[];
  params?: Record<string, any>;
  breadcrumb?: string[];
}

function cleanCustomerBreadcrumbs(crumbs?: string[], fallback = "Khách hàng"): string[] {
  if (!crumbs || crumbs.length === 0) return [fallback];
  const mapped = crumbs.map((c) => {
    if (c === "Thêm mới khách hàng" || c === "Thêm khách hàng mới" || c === "Cập nhật khách hàng") return "Khách hàng";
    return c;
  });
  return mapped.filter((c, i) => i === 0 || c !== mapped[i - 1]);
}

export default function CustomerFormModal({
  open,
  onClose,
  onSuccess,
  onCancel,
  customerId,
  mode,
  initialData,
  breadcrumb,
}: CustomerFormModalProps) {
  const queryClient = useQueryClient();
  const isEdit = !!customerId || mode === "edit";

  const handleClose = () => {
    if (onCancel) onCancel();
    if (onClose) onClose();
  };

  const handleSubmit = async (data: CustomerFormData) => {
    try {
      let result: any;
      if (customerId) {
        result = await trpcClient.customers.update.mutate({
          id: customerId,
          data,
        });
        toast.success(`Đã cập nhật khách hàng: ${result.name || result.shortName || result.code || ""}`);
      } else {
        result = await trpcClient.customers.create.mutate(data);
        toast.success(`Đã thêm khách hàng: ${result.name || result.shortName || result.code || ""}`);
      }

      await queryClient.invalidateQueries({ queryKey: ["customers"] });
      if (customerId) {
        await queryClient.invalidateQueries({ queryKey: ["customer", customerId] });
      }
      await queryClient.invalidateQueries({ queryKey: ["combobox"] });
      await queryClient.invalidateQueries({ queryKey: ["customer-lookup"] });
      await queryClient.invalidateQueries({ queryKey: ["customers-all-check"] });

      const label = result.name || result.shortName || result.code || "";

      if (onSuccess) {
        onSuccess({
          id: result.id,
          label,
          original: result,
        });
      }
      if (onClose) onClose();
    } catch (err: any) {
      toast.error(err?.message || (isEdit ? "Cập nhật khách hàng thất bại" : "Tạo khách hàng thất bại"));
    }
  };

  const effectiveBreadcrumbs = cleanCustomerBreadcrumbs(breadcrumb, "Khách hàng");

  const content = (
    <div className="w-full max-h-[85vh] overflow-y-auto px-1">
      <CustomerForm
        customerId={customerId}
        defaultValues={{
          name: initialData?.name || "",
          code: initialData?.code || "",
          ...(initialData || {}),
        }}
        onSubmit={handleSubmit}
        onCancel={handleClose}
        onUseExisting={
          isEdit || !onSuccess
            ? undefined
            : (existing: any) => {
                // Trùng MST / mã: chọn luôn khách hàng đã có cho chứng từ đang lập, không tạo mới
                toast.success(`Đã chọn khách hàng có sẵn: ${existing.name || existing.shortName || existing.code || ""}`);
                onSuccess({ id: existing.id, label: existing.name || existing.shortName || existing.code || "", original: existing });
                if (onClose) onClose();
              }
        }
        submitLabel={isEdit ? "Lưu thay đổi" : "Tạo & Chọn ngay"}
        isModal
        modalHeader={
          <ModalBreadcrumbHeader
            breadcrumbs={effectiveBreadcrumbs}
            title={isEdit ? "Cập nhật thông tin khách hàng" : "Thêm mới khách hàng"}
            description={
              isEdit
                ? "Chỉnh sửa mã số thuế, thông tin liên hệ, điều khoản thanh toán và hạn mức công nợ."
                : "Khai báo thông tin pháp nhân, mã số thuế, hạn mức công nợ khách hàng."
            }
            icon={Building2}
            iconColor="text-emerald-600"
            className="border-b-0 pb-0"
          />
        }
      />
    </div>
  );

  // When used as standalone modal with explicit open/onClose
  if (open !== undefined && onClose !== undefined) {
    return (
      <Dialog open={open} onOpenChange={(val) => !val && handleClose()}>
        <DialogContent 
          className="w-[95vw] sm:max-w-5xl lg:max-w-7xl max-h-[92vh] flex flex-col p-4 sm:p-5"
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.stopPropagation();
            }
          }}
        >
          {content}
        </DialogContent>
      </Dialog>
    );
  }

  // When embedded in ComboboxFK which supplies its own Dialog
  return content;
}
